import { getToken } from '@/utils/auth';
import { getInfo } from '@/api/user';

// 默认连接 ID 常量
const DEFAULT_CONNECTION_ID = 'default';
const DEFAULT_CONNECTION_NAME = '主连接';

/**
 * WebSocket客户端类
 * 提供自动重连、心跳检测、事件监听等功能
 */
class WebSocketClient {
  constructor(url, options = {}) {
    this.url = url;
    this.options = {
      maxReconnectAttempts: 5, // 最大重连次数
      reconnectInterval: 3000, // 重连间隔(ms)
      heartbeatInterval: 30000, // 心跳间隔(ms)
      debug: false, // 调试模式
      ...options,
    };

    // 连接状态
    this.socket = null;
    this.isConnected = false;
    this.isManualClosed = false;
    this.reconnectCount = 0;

    // 定时器
    this.reconnectTimer = null;
    this.heartbeatTimer = null;

    // 事件监听器
    this.listeners = {
      open: [],
      message: [],
      error: [],
      close: [],
    };

    this.log('WebSocket客户端已初始化');
  }

  /**
   * 日志输出
   */
  log(...args) {
    if (this.options.debug) {
      // console.log('[WebSocket]', ...args);
    }
  }

  /**
   * 连接WebSocket
   */
  connect() {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.log('WebSocket已连接，无需重复连接');
      return;
    }

    // 如果socket存在但不是OPEN状态，先清理掉
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.isManualClosed = false;

    // 构建包含cookie的URL
    // const wsUrl = this.buildWebSocketUrl();
    // this.log('正在连接WebSocket...', wsUrl);

    try {
      this.socket = new WebSocket(this.url);

      this.setupEventHandlers();
    } catch (error) {
      // this.log('WebSocket连接失败:', error);
      this.handleReconnect();
    }
  }

  /**
   * 构建包含cookie的WebSocket URL
   */
  buildWebSocketUrl() {
    // 只处理对象类型的cookies
    if (this.options.cookies && typeof this.options.cookies === 'object') {
      const url = '';
      // 将cookies对象转换为JSON字符串
      const cookiesJson = JSON.stringify(this.options.cookies);
      url.searchParams.append('cookies', encodeURIComponent(cookiesJson));
      return url.toString();
    }
    return url;
  }

  /**
   * 设置事件处理器
   */
  setupEventHandlers() {
    this.socket.onopen = () => {
      this.isConnected = true;
      this.reconnectCount = 0;
      this.log('WebSocket连接成功');

      this.startHeartbeat();
      this.emit('open');
    };

    this.socket.onmessage = (event) => {
      // 处理心跳回复
      if (event.data === 'pong') {
        // this.log('收到心跳回复');
        return;
      }

      // this.log('收到消息:', event.data);
      this.emit('message', event.data);
    };

    this.socket.onerror = (error) => {
      // this.log('WebSocket错误:', error);
      this.emit('error', error);
    };

    this.socket.onclose = (event) => {
      this.isConnected = false;
      this.stopHeartbeat();

      // this.log('WebSocket连接关闭:', {
      //   code: event.code,
      //   reason: event.reason,
      //   wasClean: event.wasClean,
      // });

      this.emit('close', event);

      // 非手动关闭且非正常关闭时尝试重连
      if (!this.isManualClosed && (!event.wasClean || event.code !== 1000)) {
        this.handleReconnect();
      }
    };
  }

  /**
   * 启动心跳
   */
  startHeartbeat() {
    this.stopHeartbeat();

    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send('ping');
        // this.log('发送心跳');
      } else {
        // this.log('心跳检测失败，连接状态异常');
        this.handleReconnect();
      }
    }, this.options.heartbeatInterval);
  }

  /**
   * 停止心跳
   */
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * 处理重连
   */
  async handleReconnect() {
    if (this.isManualClosed) {
      // this.log('连接已手动关闭，不进行重连');
      return;
    }

    if (this.reconnectTimer) {
      return; // 已在重连中
    }

    this.reconnectCount++;

    if (this.reconnectCount > this.options.maxReconnectAttempts) {
      // this.log(`重连失败，已达到最大重连次数: ${this.options.maxReconnectAttempts}`);
      return;
    }

    // this.log(`准备重连... (${this.reconnectCount}/${this.options.maxReconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, this.options.reconnectInterval);
  }

  /**
   * 发送消息
   */
  send(data) {
    if (!this.isConnected || this.socket.readyState !== WebSocket.OPEN) {
      // this.log('连接未就绪，尝试重新连接...');

      // 尝试重新连接
      this.connect();

      // 等待连接建立后发送消息
      const checkConnection = () => {
        if (this.isConnected && this.socket.readyState === WebSocket.OPEN) {
          const message =
            typeof data === 'string' ? data : JSON.stringify(data);
          this.socket.send(message);
          this.log('发送消息:', message);
        } else if (this.reconnectCount < this.options.maxReconnectAttempts) {
          // 如果还在重连中，继续等待
          setTimeout(checkConnection, 1000);
        } else {
          // this.log('重连失败，无法发送消息');
          throw new Error('WebSocket连接失败，无法发送消息');
        }
      };

      // 延迟检查连接状态
      setTimeout(checkConnection, 100);
      return;
    }

    const message = typeof data === 'string' ? data : JSON.stringify(data);
    this.socket.send(message);
    // this.log('发送消息:', message);
  }

  /**
   * 关闭连接
   */
  close() {
    this.isManualClosed = true;
    this.stopHeartbeat();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    // this.log('WebSocket连接已关闭');
  }

  /**
   * 添加事件监听器
   */
  on(event, listener) {
    if (this.listeners[event]) {
      this.listeners[event].push(listener);
    }
  }

  /**
   * 移除事件监听器
   */
  off(event, listener) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(
        (l) => l !== listener
      );
    }
  }

  /**
   * 触发事件
   */
  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach((listener) => {
        try {
          listener(data);
        } catch (error) {
          // this.log('事件监听器执行错误:', error);
        }
      });
    }
  }

  /**
   * 获取连接状态
   */
  getReadyState() {
    return this.socket ? this.socket.readyState : WebSocket.CLOSED;
  }

  /**
   * 是否已连接
   */
  get connected() {
    return (
      this.isConnected &&
      this.socket &&
      this.socket.readyState === WebSocket.OPEN
    );
  }

  /**
   * 兼容性方法 - 添加消息监听器
   */
  addMessageListener(listener) {
    this.on('message', listener);
  }

  /**
   * 兼容性方法 - 移除消息监听器
   */
  removeMessageListener(listener) {
    this.off('message', listener);
  }

  /**
   * 兼容性方法 - 手动关闭连接
   */
  manualClose() {
    this.close();
  }
}

/**
 * 获取 store 实例
 */
function getStore() {
  try {
    const store = require('@/store').default;
    return store;
  } catch (error) {
    // console.error('无法获取 store 实例:', error);
    throw new Error('store 实例获取失败，WebSocket 无法正常工作');
  }
}

/**
 * 初始化WebSocket连接
 */
export function initWebSocket(options = {}) {
  const store = getStore();
  let url = `${
    process.env.VUE_APP_SOCKET_URL
  }resource/websocket?Authorization=${getToken()}`;

  const connectionOptions = {
    heartbeatInterval: 20000,
    ...options,
  };

  // 检查是否已存在默认连接
  const existingConnection = store.getters['socket/getConnection'](
    DEFAULT_CONNECTION_ID
  );
  const isConnected = store.getters['socket/isConnected'](
    DEFAULT_CONNECTION_ID
  );

  if (existingConnection && isConnected) {
    // console.log('默认WebSocket连接已存在且正常，复用现有连接');
    // 如果连接已存在且正常，也要触发 onOpen 回调
    if (options.onOpen && typeof options.onOpen === 'function') {
      options.onOpen();
    }
    return Promise.resolve(existingConnection);
  }

  // 创建新的默认连接
  // console.log('创建新的默认WebSocket连接');
  return store
    .dispatch('socket/createConnection', {
      info: {
        id: DEFAULT_CONNECTION_ID,
        name: DEFAULT_CONNECTION_NAME,
      },
      url,
      options: connectionOptions,
    })
    .then((connection) => {
      console.log('默认WebSocket连接创建成功', connection, options);
      if (options.onOpen && typeof options.onOpen === 'function') {
        options.onOpen();
      }
      return connection;
    })
    .catch((error) => {
      // console.error('创建默认WebSocket连接失败:', error);
      throw error;
    });
}

/**
 * 关闭WebSocket连接
 */
export function closeWebSocket() {
  const store = getStore();
  store.dispatch('socket/closeConnection', DEFAULT_CONNECTION_ID);
  // console.log('默认WebSocket连接已关闭');
}

/**
 * 发送消息（默认连接）
 */
export function sendMessage(data) {
  const store = getStore();

  try {
    return store.dispatch('socket/sendMessage', {
      id: DEFAULT_CONNECTION_ID,
      data,
    });
  } catch (error) {
    // console.warn('发送消息失败，尝试重新初始化连接...', error);
    return initWebSocket().then(() => {
      return store.dispatch('socket/sendMessage', {
        id: DEFAULT_CONNECTION_ID,
        data,
      });
    });
  }
}

/**
 * 为指定连接添加消息监听器
 */
export function addMessageListenerToConnection(connectionId, listener) {
  const store = getStore();
  const connection = store.getters['socket/getConnection'](connectionId);
  if (connection) {
    connection.on('message', listener);
  }
}

export default WebSocketClient;
